import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useCreateDialog } from "../hooks/useCreateDialog";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import GroundPicker from "./GroundPicker";
import FacilityPicker, { useGroundFacilities } from "./FacilityPicker";
import InlineFacilityRating from "./InlineFacilityRating";
import { groundFieldError } from "../lib/groundRef";
import { facilityAfterGroundChange, facilityOptionLabel } from "../lib/facilities";
import { draftFromOwnRating, facilityRatingPath, ratingBodyFromDraft, ratingDraftComplete } from "../lib/facilityRatings";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { TriangleAlert } from "lucide-react";
import { FieldError, MoreSection } from "./kit";
import {
  BoardEmpty, BoardFilteredEmpty, BoardLoadFailed, BoardLoading, BoardSessionCard,
  CoachSaveFailed, CoachScheduledLine, CoachSessionsList,
} from "./TrainingSessionsBoard";
import {
  Plus, Pencil, Trash2, Clock, MapPin, User, Calendar, Target,
  Sparkles, Loader2, X, Flame, Zap, Smile, Brain, CalendarPlus, Users, Grid2x2,
} from "lucide-react";
import { toast } from "sonner";
import { canSubmitRequired, useRequiredFields } from "../lib/requiredFields";
import { useFocusFirstInvalid } from "../lib/focusFirstInvalid";
import {
  ALL_CONTEXTS,
  DEFAULT_CONTEXT,
  SCHEDULE_LOCKED_NOTICE,
  SESSION_CONTEXTS,
  contextFields,
  contextLabel,
  contextPayload,
  isScheduleLocked,
  validateContext,
  withoutLockedFields,
} from "../lib/sessionContext";
import {
  COMPLETION_PROMPT,
  PLANNED_HINT,
  completionPayload,
  isPlanned,
  needsReflection,
  showsReflection,
  statusBadge,
  clientDateHeaders,
  todayIso,
  withoutReflectionFields,
} from "../lib/trainingStatus";
import { dateTimeLabel } from "../lib/clockTime";
import { planDateMax, plannedSessionDateError, refusalToast } from "../lib/comingUp";
import { track } from "../lib/usageTracker";

const NAVY = "#0B2E59", GOLD = "#D4A017", GREEN = "#10B981", BLUE = "#84C5FF", PURPLE = "#8B5CF6";

// Issue #142: same pass applied here as PerformanceTracking.jsx — checked
// this form's client validation against `trainingSessionCreateSchema`
// (api/src/routes/trainingSessions.ts). `session_title` was the only
// field actually checked; `training_date` and `category` are required by
// the API too but both start pre-filled with sane defaults (today's date,
// "Batting") and looked safe — until you notice `training_date` is a
// native `<input type="date">`, which a user CAN clear by hand, at which
// point this form had the exact same gap #142 reports for matches: passes
// the client check, 400s at the API, generic toast. Fixed the same way,
// reusing the same requiredFields.js mechanism.
const FIELD_LABELS = { session_title: "Session title", training_date: "Training date", category: "Training category" };

// Issue #106: default control height (h-9, 36px) is a mouse-sized target.
// Bump to ~44px on phones where this form is actually used (at the ground);
// hand back to the original density at `sm:` so desktop is unchanged.
const TOUCH = "h-11 sm:h-9";
// #1305 (D303): a missing field on the new look is the 2px alert-red border. As
// kit `Field`; it sets no outline or shadow, so the Input's own keyboard ring
// (#1334) still draws beside the red while the field is focused (Save moves
// focus here).
// #1334: the keyboard ring reaches 4px out and the dialog body clips at the field edge, so the
// new look gives the body 4px of room each side (px-1) and pulls it back out (-mx-1).
const NEW_LOOK_RING_ROOM = "px-1 -mx-1";
const INVALID_STYLE = { border: "var(--nw-border-invalid)" };
// The Capture session / Schedule a session trigger: navy on the shipped page,
// the board's one gold, 52px, on the two-section look (#1128).
const OLD_TRIGGER_LOOK = { className: TOUCH, style: { background: NAVY } };
const NEW_TRIGGER_LOOK = { className: "h-[52px] sm:h-11 text-base font-bold", style: { background: GOLD, color: "#0F172A" } };
const TOUCH_ICON = "h-11 w-11 sm:h-9 sm:w-9";

// Issue #152: this is the SKILL axis and it stays that way. "Team training"
// does NOT belong in this list — who a session is with is `session_context`,
// a separate control, because team batting training is an ordinary thing to
// record and one list would make the two mutually exclusive. There is a unit
// test over this array asserting the two lists never overlap.
const CATEGORIES = ["Batting", "Bowling", "Fielding", "Wicket Keeping", "Mindset", "Training Drill"];
const CATEGORY_COLORS = {
  "Batting": NAVY, "Bowling": GOLD, "Fielding": BLUE,
  "Wicket Keeping": PURPLE, "Mindset": GREEN, "Training Drill": "#EC4899",
};

const empty = {
  session_context: DEFAULT_CONTEXT, squad_name: "",
  session_title: "", training_date: todayIso(),
  // #463. Empty by default and empty is legal: a coach who knows the day
  // but not the hour still schedules. Never pre-filled with a guess — a
  // default like 09:00 would be this form inventing a fact.
  training_time: "",
  // #356: the label and the record it names, held together — see
  // `lib/groundRef.js`. A training session may legitimately have neither.
  // #166/#423: the surface, which only exists as half of a pair with
  // `ground_id`. Cleared whenever the ground changes — see
  // `facilityAfterGroundChange` in `lib/facilities.js`.
  coach_name: "", location: "", ground_id: null, facility_id: null, duration_minutes: 60,
  category: "Batting", skill_topic: "",
  lesson_learned: "", what_worked_well: "", areas_for_improvement: "", next_focus_area: "",
  confidence_rating: 7, focus_rating: 7, motivation_rating: 8,
  mindset_reflection: "", overall_reflection: "",
  action_items: [""],
};

// Issue #127: unlike PerformanceTracking's 18 counters, `duration_minutes`
// keeps a useful non-zero default (60) — a genuinely blank duration should
// fall back to that default at submit, not silently become 0 minutes
// (which the API's own repo-layer default already means "not provided" —
// see api/src/db/repos/trainingSessions.ts createTrainingSession). While
// typing, though, the field must behave like any of them: hold the raw
// string so it can be cleared and retyped without appending ("6045").
function sanitizeDuration(v) {
  if (v === "" || v === null || v === undefined) return "";
  return String(v).replace(/[^\d]/g, "");
}
function toDurationMinutes(v) {
  if (v === "" || v === null || v === undefined) return 60;
  const n = parseInt(String(v).replace(/[^\d]/g, ""), 10);
  return Number.isFinite(n) ? n : 60;
}

// Issue #75: "coaches should only be able to schedule a training session" —
// a coach may put a session in a linked player's calendar, but the
// reflection belongs to the player who did it. The server is what actually
// enforces this (api/src/routes/trainingSessions.ts rejects any reflection
// field a coach's payload carries, 403, independent of what this component
// renders) — this component's job is just to not offer a coach fields they
// aren't allowed to fill in, and to select which linked player a scheduled
// session is for.
export default function TrainingSessions({ playerId, newLook = false } = {}) {
  const { user } = useAuth();
  const isCoach = user?.role === "coach";
  const [sp] = useSearchParams();

  const [items, setItems] = useState([]);
  // #1281: "loading" | "ready" | "failed". Only the new look's player cards read
  // it; the old look still renders `items` alone (D217).
  const [loadState, setLoadState] = useState("loading");
  // #1159: the player the current `items` were read for (a coach reads one at a
  // time), so a roster that has just arrived is "loading" until that player's
  // read has come back, not a one-frame "no sessions".
  const [loadedFor, setLoadedFor] = useState(null);
  // #1281: the board's cards are a player on the new look only. `newLook` is
  // also true for a coach on /learn (#1225 owns that body).
  const boardCards = newLook && !isCoach;
  // #1159 (CoachTrainingSessions board, round 12, D298): the coach's Sessions
  // section: its own cards and states, the player picker on the face, and a
  // line that stays instead of a toast. The old look keeps `SessionCard` (D217).
  const coachBoard = newLook && isCoach;
  // The line that replaces the "Session scheduled!" toast (D48), and the
  // failure that stays in the sheet (D57), for the coach's board only.
  const [scheduledLine, setScheduledLine] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [filter, setFilter] = useState("all");
  // Issue #152: a second, independent filter. Two dimensions in the data and
  // one control would mean the differentiation exists everywhere except where
  // a player can see it.
  const [contextFilter, setContextFilter] = useState(ALL_CONTEXTS);
  // Issue #79: `?new=1` (i.e. `/learn?tab=training&new=1`) opens this dialog
  // on arrival, so the Dashboard's activity chooser lands a player in the
  // session form rather than beside the button that opens it. Same
  // `[open, setOpen]` as the plain useState it replaces — `closeDialog`
  // below is unchanged, and so is every other caller.
  const [open, setOpen] = useCreateDialog();
  const [f, setF] = useState(empty);
  // #581 — the optional net/field rating captured with a self-logged
  // session. Held apart from `f` so it never rides into the session payload;
  // the surface is looked up from the venue list so the write body knows
  // which axes exist (Turn & Grip on turf only). See `save` for the
  // POST-then-PUT ordering the played-here gate forces.
  const [ratingDraft, setRatingDraft] = useState(() => draftFromOwnRating(null));
  const ratingFacilities = useGroundFacilities(f.ground_id);
  const selectedFacility = f.facility_id
    ? ratingFacilities.find((x) => x.facility_id === f.facility_id) || null
    : null;
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // The row being edited, kept whole so the dialog can tell a booked session
  // from a self-logged one (#152 AC 5) — `booking_id` is the flag, and it is
  // read-only on the wire in both directions.
  const [editing, setEditing] = useState(null);
  const locked = isScheduleLocked(editing);
  // A coach is scheduling 1:1 coaching by definition (#152), so they get no
  // selector at all — the server forces the same value regardless.
  const ctxFields = contextFields(isCoach ? "coaching" : f.session_context);
  // Issue #83. One rule decides whether this dialog asks how the session
  // went (see `trainingStatus.js`): a future date means it hasn't happened,
  // so the reflection block is HIDDEN rather than shown-and-optional. A
  // coach never gets it at all (#75).
  const showReflection = !isCoach && showsReflection(f, editing);
  // Editing a planned session whose date has passed IS the completion flow —
  // same dialog, one extra sentence, and saving flips the status.
  const completing = !isCoach && isPlanned(editing) && f.training_date <= todayIso();
  // Creating (or re-dating) something that hasn't happened yet.
  const planningAhead = !isCoach && !showReflection;
  // Issue #142: fetched from the API, not hand-typed — see requiredFields.js.
  // Issue #783: loading, known or unknown; only "loading" disables Save.
  const required = useRequiredFields("/training-sessions/required-fields");
  const [fieldErrors, setFieldErrors] = useState({});
  // #1385 (D328): the keys Save refused as MISSING. A date past the cap or an
  // unresolved ground is a bad value, not a missing field, so neither is
  // counted. The summary line counts the ones still marked.
  const [missingKeys, setMissingKeys] = useState([]);
  const missingCount = missingKeys.filter((k) => fieldErrors[k]).length;
  // #1305 (D303): on the new look Save takes the player to the first missing
  // field. `setFieldErrors(errors)` is a new object on every failed Save, so a
  // second Save with the field still empty moves focus again.
  const bodyRef = useRef(null);
  const armFocus = useFocusFirstInvalid(bodyRef, fieldErrors);
  // The new look's marks for one field: ARIA, the sentence id and the invalid
  // border. `{}` on the old look and on a valid field, so those render as
  // before (D217). `oldRed` is the old look's Tailwind class, kept as is.
  const invalidProps = (key, id) =>
    newLook && fieldErrors[key] ? { "aria-invalid": "true", "aria-describedby": id, "data-nw-invalid": "", style: INVALID_STYLE } : {};
  const oldRed = (key, cls) => (!newLook && fieldErrors[key] ? cls : "");
  const fieldError = (key, id, oldClass) =>
    !fieldErrors[key] ? null : newLook ? (
      <FieldError id={id} testId={id}>{fieldErrors[key]}</FieldError>
    ) : (
      <p className={oldClass} data-testid={id}>{fieldErrors[key]}</p>
    );

  // Coach-only: which linked player this dialog is scheduling for. Prefers
  // an explicit `playerId` prop (for a future roster-embedded usage), then
  // a `?player=<id>` deep link (same param CoachPortal's roster cards
  // already set), then the first row of the coach's own roster.
  const [roster, setRoster] = useState([]);
  const [coachPlayerId, setCoachPlayerId] = useState(playerId || "");
  // #1159: "loading" | "ready" | "failed". A failed roster read is a failed
  // list, never "you have no linked players" (D53 ruling 2).
  const [rosterState, setRosterState] = useState("loading");
  const loadRoster = useCallback(() => {
    setRosterState("loading");
    api.get("/players").then((r) => {
      setRoster(r.data);
      setCoachPlayerId((current) => current || playerId || sp.get("player") || (r.data[0]?.player_id ?? ""));
      setRosterState("ready");
    }).catch(() => setRosterState("failed"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!isCoach) return;
    loadRoster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCoach]);
  const effectivePlayerId = isCoach ? coachPlayerId : null;

  // #1159: a reply that lands after a newer request was made (the coach switched
  // player, or changed a filter) is ignored, so the list never shows the wrong
  // player's sessions and `loadedFor` can never be left behind.
  const loadSeq = useRef(0);
  const load = useCallback(() => {
    const seq = ++loadSeq.current;
    if (isCoach && !effectivePlayerId) { setItems([]); setLoadState("ready"); return; }
    const params = new URLSearchParams();
    if (filter !== "all") params.set("category", filter);
    if (contextFilter !== ALL_CONTEXTS) params.set("session_context", contextFilter);
    if (isCoach) params.set("player_id", effectivePlayerId);
    const qs = params.toString();
    setLoadState("loading");
    api.get(`/training-sessions${qs ? `?${qs}` : ""}`, { headers: clientDateHeaders() })
      .then((r) => { if (seq !== loadSeq.current) return; setItems(r.data); setLoadedFor(effectivePlayerId); setLoadState("ready"); })
      .catch(() => { if (seq === loadSeq.current) setLoadState("failed"); });
  }, [filter, contextFilter, isCoach, effectivePlayerId]);
  useEffect(() => { load(); }, [load]);
  // The scheduled line goes on the next load: another player, another filter.
  useEffect(() => { setScheduledLine(null); }, [coachPlayerId, filter, contextFilter]);
  const retryCoach = () => { setScheduledLine(null); loadRoster(); load(); };

  const save = async () => {
    // Belt-and-braces: `ts-save` is `disabled` only while the required-fields
    // fetch is IN FLIGHT (see the Button below), so a real click can no longer
    // land here before it's loaded. Kept as a silent guard against a
    // non-click caller (a stray double-submit, a future keyboard shortcut)
    // rather than relied on as the fix (found via CI journey 14 racing this
    // fetch under load; journey 14b proves the in-flight half). Issue #783:
    // when the fetch FAILED the status is "unknown", Save is enabled and the
    // server's own 400 answers (journey 8u).
    if (!canSubmitRequired(required.status)) return;
    if (newLook) armFocus();
    const errors = {};
    for (const key of required.fields) {
      const val = f[key];
      if (!val || !String(val).trim()) errors[key] = `${FIELD_LABELS[key] || key} is required`;
    }
    // #1245 (D263): a planned session is capped at today + 365 days; past
    // sessions are not. The input's `max` is a hint a typed date walks past.
    // The API only checks a date that is being set, so an older row's untouched
    // date never blocks an edit here either.
    const dateMoved = !editingId || f.training_date !== editing?.training_date;
    const dateError = dateMoved ? plannedSessionDateError(f.training_date, todayIso()) : null;
    const rangeRefused = Boolean(dateError) && !errors.training_date;
    if (rangeRefused) errors.training_date = dateError;
    // Issue #152: the context rules are the same ones the API enforces
    // (`routes/trainingSessions.ts`); running them here turns a 400 toast into
    // a highlighted field. A coach's forced `coaching` context means the coach
    // name is required of them too.
    Object.assign(errors, validateContext({ ...f, session_context: isCoach ? "coaching" : f.session_context }));
    /**
     * Issue #356: a location is still OPTIONAL here (unlike a match's
     * ground) — `required: false` is that asymmetry, and it is the API's
     * rule rather than this form's. What is not optional is that a location
     * which IS given names a real ground.
     */
    const locationError = groundFieldError(f.location, f.ground_id, { required: false });
    if (locationError) errors.location = locationError;
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setMissingKeys(Object.keys(errors).filter((k) => k !== "location" && !(k === "training_date" && rangeRefused)));
      // A date past the cap is not a missing field: the field's own sentence is
      // the message, so the "fill in the required fields" toast is skipped when
      // that is the only refusal (#1245).
      // #1305 (D57): on the new look the fields are marked and focused, so no toast.
      if (newLook) return;
      return refusalToast(errors, rangeRefused) && toast.error("Please fill in the required fields, highlighted below");
    }
    setFieldErrors({});
    if (isCoach && !effectivePlayerId) return toast.error("Select a player to schedule for");
    setSaveError(null);
    setBusy(true);
    try {
      // Scheduling fields only for a coach — never the reflection/outcome
      // fields, even though `f` may hold their (unused) default values.
      // This is a UX courtesy, not the enforcement boundary: the API
      // rejects those fields from a coach regardless of what any client
      // sends.
      const payload = isCoach
        ? {
            player_id: effectivePlayerId,
            session_title: f.session_title,
            training_date: f.training_date,
            // #463 — the field this ticket exists for: a coach scheduling a
            // session can now say when on the day. `""` when they did not,
            // which the API folds to null.
            training_time: f.training_time,
            coach_name: f.coach_name,
            // #356: the RECORD is what the API stores; the label it derives
            // from that record is what comes back. A coach's scheduling
            // payload carries it for the same reason a player's does.
            ground_id: f.ground_id,
            // #166: the other half of the pair. Null unless the venue has
            // registered surfaces AND one was chosen — which is the ordinary
            // case, and the API's ordinary case too.
            facility_id: f.ground_id ? f.facility_id : null,
            location: f.location,
            duration_minutes: toDurationMinutes(f.duration_minutes),
            category: f.category,
            skill_topic: f.skill_topic,
            // No `session_context` from a coach: the server decides it, and a
            // client sending one would only be describing what it thinks the
            // rule is (#152).
          }
        : withoutLockedFields(
            withoutReflectionFields(
              {
                ...f,
                // #166: never a surface without its venue. `...f` already
                // carries the key; this is the pair rule said once more where
                // the payload is built, because the spread makes it invisible.
                facility_id: f.ground_id ? f.facility_id : null,
                ...contextPayload(f),
                duration_minutes: toDurationMinutes(f.duration_minutes),
                action_items: f.action_items.map((s) => s.trim()).filter(Boolean),
                // #83: asks to complete a planned session whose date has
                // passed, and is `{}` in every other case. The API decides
                // `status` from the date on create and refuses the reverse
                // transition, so this can only ever say "it happened".
                ...completionPayload(editing, f),
              },
              showReflection,
            ),
            // Only meaningful on an edit; a session being created has no
            // booking to be locked by.
            locked,
          );
      let saved;
      if (editingId) {
        const res = await api.put(`/training-sessions/${editingId}`, payload, { headers: clientDateHeaders() });
        saved = res.data;
        toast.success("Training session updated!");
      } else {
        const res = await api.post("/training-sessions", payload, { headers: clientDateHeaders() });
        saved = res.data;
        if (coachBoard) {
          const who = roster.find((p) => p.player_id === effectivePlayerId)?.full_name || "your player";
          setScheduledLine(`Session scheduled for ${who} on ${f.training_date}.`);
        } else {
          toast.success(isCoach ? "Session scheduled!" : "Training session saved!");
        }
      }
      // #581 — the optional net/field rating rides AFTER the session row
      // exists and off the SAVED row's ids: the played-here gate reads logged
      // sessions, so a rating PUT before the POST 403s. A coach never rates
      // here (they are not the player), and a failed rating must not lose the
      // saved session — it is already toasted as saved, so this only adds a
      // second, non-fatal toast. On an edit the row exists, so order is moot.
      if (!isCoach && saved?.ground_id && saved?.facility_id && selectedFacility && ratingDraftComplete(ratingDraft)) {
        try {
          await api.put(
            facilityRatingPath(saved.ground_id, saved.facility_id),
            ratingBodyFromDraft(ratingDraft, selectedFacility.surface),
          );
        } catch {
          toast.error("Session saved — rating not saved. You can rate this net or field from Grounds, or try again.");
        }
      }
      setOpen(false); setEditingId(null); setEditing(null); setF(empty); setRatingDraft(draftFromOwnRating(null)); load();
    } catch (e) {
      if (coachBoard) setSaveError(true);
      else toast.error(e?.response?.data?.detail || (editingId ? "Failed to update" : "Failed to save"));
    }
    setBusy(false);
  };

  const openEdit = (s) => {
    setFieldErrors({});
    setEditingId(s.session_id);
    setEditing(s);
    // #581 — the inline rating opens empty on an edit (no prefill); the PUT
    // is an upsert, so a verdict cast here updates the one rating row.
    setRatingDraft(draftFromOwnRating(null));
    setF({
      session_context: s.session_context || DEFAULT_CONTEXT,
      squad_name: s.squad_name || "",
      session_title: s.session_title || "",
      training_date: s.training_date || todayIso(),
      // #463. `""` for a session with no time — including every session
      // that predates the column — so the control opens blank rather than
      // showing a time nobody set.
      training_time: s.training_time || "",
      coach_name: s.coach_name || "",
      location: s.location || "",
      // #356: null on a session that predates ground records. The picker
      // resolves the stored name against the directory on load, so a
      // session at a known ground opens already valid.
      ground_id: s.ground_id ?? null,
      // #166. Loaded as stored; the picker below shows it selected when the
      // venue's surfaces can be read, and read-only when they cannot.
      facility_id: s.facility_id ?? null,
      duration_minutes: s.duration_minutes ?? 60,
      category: s.category || "Batting",
      skill_topic: s.skill_topic || "",
      lesson_learned: s.lesson_learned || "",
      what_worked_well: s.what_worked_well || "",
      areas_for_improvement: s.areas_for_improvement || "",
      next_focus_area: s.next_focus_area || "",
      confidence_rating: s.confidence_rating ?? 7,
      focus_rating: s.focus_rating ?? 7,
      motivation_rating: s.motivation_rating ?? 8,
      mindset_reflection: s.mindset_reflection || "",
      overall_reflection: s.overall_reflection || "",
      action_items: s.action_items?.length ? s.action_items : [""],
    });
    setOpen(true);
  };

  const closeDialog = (isOpen) => {
    setOpen(isOpen);
    setFieldErrors({});
    setSaveError(null);
    if (!isOpen) { setEditingId(null); setEditing(null); setF(empty); setRatingDraft(draftFromOwnRating(null)); }
  };

  const remove = async (s) => {
    if (!confirm("Delete this session?")) return false;
    await api.delete(`/training-sessions/${s.session_id}`);
    load();
    return true;
  };

  const setActionItem = (idx, val) => {
    const arr = [...f.action_items]; arr[idx] = val;
    setF({ ...f, action_items: arr });
  };
  const addActionItem = () => setF({ ...f, action_items: [...f.action_items, ""] });
  const removeActionItem = (idx) => {
    const arr = [...f.action_items]; arr.splice(idx, 1);
    setF({ ...f, action_items: arr.length ? arr : [""] });
  };

  const triggerLook = newLook ? NEW_TRIGGER_LOOK : OLD_TRIGGER_LOOK;
  // The create/edit dialog and its trigger. One element, placed by whichever
  // layout is showing (#1128): inside the filter row for the shipped page, or
  // leading the section for the two-section look. Its body is untouched.
  const dialog = (
          <Dialog open={open} onOpenChange={closeDialog}>
            <DialogTrigger asChild>
              <Button className={`w-full sm:w-auto ${triggerLook.className}`} style={triggerLook.style} disabled={isCoach && roster.length === 0} data-testid="ts-new" onClick={() => { setScheduledLine(null); track("training.new_session"); }}>
                {isCoach ? <CalendarPlus size={16} className="mr-2" /> : <Plus size={16} className="mr-2" />}
                {isCoach ? "Schedule a session" : "Capture session"}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl" bodyClassName={newLook ? NEW_LOOK_RING_ROOM : undefined}>
              <DialogHeader>
                <DialogTitle>
                  {dialogTitle({ completing, editingId, isCoach, planningAhead })}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4" ref={bodyRef}>
                {/* #1385 (D328): the board's on-page summary line, new look only
                    (D217). D303's red triangle replaces the board's info glyph. */}
                {/* The live region stays mounted (empty) so a screen reader
                    announces the sentence when it is rendered into it; a region
                    created with its text is often silent. `empty:-mb-4` cancels
                    the space-y-4 gap the empty region would otherwise add. */}
                {newLook && (
                  <div role="status" className="empty:-mb-4">
                    {missingCount > 0 && (
                      <p
                        data-testid="ts-form-error"
                        style={{
                          display: "flex",
                          gap: 8,
                          padding: 12,
                          background: "var(--nw-surface)",
                          border: "2px solid var(--nw-ink)",
                          borderRadius: "var(--nw-radius-control)",
                          fontSize: 13,
                          fontWeight: 600,
                          lineHeight: 1.5,
                          color: "var(--nw-ink)",
                        }}
                      >
                        <TriangleAlert size={15} color="var(--nw-alert)" strokeWidth={2} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
                        <span>{`Please fill in the ${missingCount} required ${missingCount === 1 ? "field" : "fields"}, highlighted below`}</span>
                      </p>
                    )}
                  </div>
                )}
                {/* Issue #152: context is the FIRST field, above the title,
                    because it changes what the rest of the form asks for — it
                    cannot sit below the fields it governs. A coach gets no
                    selector: scheduling for a linked player IS 1:1 coaching,
                    and the server writes `coaching` regardless, so offering a
                    choice would only be offering a lie. */}
                {!isCoach && (
                  <div data-testid="ts-context-block">
                    <Label>What kind of training?</Label>
                    <Select
                      value={f.session_context}
                      onValueChange={(v) => { setF({ ...f, session_context: v }); setFieldErrors((err) => ({ ...err, coach_name: undefined })); }}
                      disabled={locked}
                    >
                      <SelectTrigger data-testid="ts-context" className={`mt-1.5 ${TOUCH}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SESSION_CONTEXTS.map((c) => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">
                      {SESSION_CONTEXTS.find((c) => c.value === f.session_context)?.hint}
                    </p>
                  </div>
                )}

                {/* The booked-session state (#152 AC 5 / #146). Nothing sets
                    `booking_id` yet, so today this never renders — it is built
                    now because retrofitting it later means a second form. */}
                {locked && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="ts-locked-notice">
                    <p className="text-xs text-slate-600">{SCHEDULE_LOCKED_NOTICE}</p>
                  </div>
                )}

                {/* #83: the completion flow is this same dialog, so the one
                    thing it needs is a sentence saying what saving will do —
                    a status flip is otherwise invisible. */}
                {completing && (
                  <div className="rounded-lg border p-3" style={{ borderColor: `${GOLD}55`, background: `${GOLD}12` }} data-testid="ts-completion-prompt">
                    <p className="text-xs text-slate-700">{COMPLETION_PROMPT}</p>
                  </div>
                )}

                {/* Meta — scheduling fields. Shown to both roles; a coach's
                    dialog stops here (reflection block below is player-only). */}
                <div className="grid md:grid-cols-2 gap-3">
                  {isCoach && roster.length > 1 && (
                    <div className="md:col-span-2"><Label>Player *</Label>
                      <Select value={coachPlayerId} onValueChange={setCoachPlayerId}>
                        <SelectTrigger data-testid="ts-dialog-player" className={`mt-1.5 ${TOUCH}`}><SelectValue placeholder="Select player" /></SelectTrigger>
                        <SelectContent>
                          {roster.map((p) => <SelectItem key={p.player_id} value={p.player_id}>{p.full_name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div><Label>Session title{required.fields.includes("session_title") && " *"}</Label>
                    <Input
                      data-testid="ts-title" value={f.session_title}
                      onChange={(e) => { setF({ ...f, session_title: e.target.value }); setFieldErrors((err) => ({ ...err, session_title: undefined })); }}
                      placeholder="e.g. Middle-order batting nets"
                      className={`mt-1.5 ${TOUCH} ${oldRed("session_title", "border-red-500 focus-visible:ring-red-500")}`}
                      {...invalidProps("session_title", "ts-title-error")} />
                    {fieldError("session_title", "ts-title-error", "text-xs text-red-600 mt-1")}
                  </div>
                  <div><Label>Training date{required.fields.includes("training_date") && " *"}</Label>
                    <Input
                      data-testid="ts-date" type="date" value={f.training_date} max={planDateMax(todayIso())} disabled={locked}
                      onChange={(e) => { setF({ ...f, training_date: e.target.value }); setFieldErrors((err) => ({ ...err, training_date: undefined })); }}
                      className={`mt-1.5 ${TOUCH} ${oldRed("training_date", "border-red-500 focus-visible:ring-red-500")}`}
                      {...invalidProps("training_date", "ts-date-error")} />
                    {fieldError("training_date", "ts-date-error", "text-xs text-red-600 mt-1")}
                    {/* #83: says why the rest of the form just got shorter.
                        A form that silently drops half its fields reads as
                        broken, not as considerate. */}
                    {planningAhead && !fieldErrors.training_date && (
                      <p className="text-xs text-slate-500 mt-1" data-testid="ts-planned-hint">{PLANNED_HINT}</p>
                    )}
                  </div>
                  {/* #463. OPTIONAL, and the label says so rather than
                      leaving a bare field a coach has to guess about: the
                      whole ticket is that a session could be scheduled for a
                      day with no way to say when on it, and the fix must not
                      turn into a new thing you are forced to fill in. It is
                      not in the required list (the API's schema marks it
                      optional and this form reads that list, #142), so no
                      `*` can ever appear here. `disabled={locked}` for the
                      date field's reason — a booking owns the whole "when". */}
                  <div><Label>Start time <span className="text-slate-400 font-normal">(optional)</span></Label>
                    <Input
                      data-testid="ts-time" type="time" value={f.training_time} disabled={locked}
                      onChange={(e) => setF({ ...f, training_time: e.target.value })}
                      className={`mt-1.5 ${TOUCH}`} />
                  </div>
                  {/* Hidden on `casual` — Martin, 2026-08-06: "No need to add
                      who with." Required on `coaching`, where a session with
                      nobody named records nothing; the API 400s it either way. */}
                  {ctxFields.coachName !== "hidden" && (
                    <div><Label>Coach name{ctxFields.coachName === "required" && " *"}</Label>
                      <Input
                        data-testid="ts-coach" value={f.coach_name} disabled={locked}
                        onChange={(e) => { setF({ ...f, coach_name: e.target.value }); setFieldErrors((err) => ({ ...err, coach_name: undefined })); }}
                        placeholder="e.g. Coach Ravi Menon"
                        className={`mt-1.5 ${TOUCH} ${oldRed("coach_name", "border-red-500 focus-visible:ring-red-500")}`}
                        {...invalidProps("coach_name", "ts-coach-error")} />
                      {fieldError("coach_name", "ts-coach-error", "text-xs text-red-600 mt-1")}
                    </div>
                  )}
                  {/* Free text, `team` only. Not a relation to anything: there
                      is no club or team entity (#103/#158 are parked). */}
                  {ctxFields.squadName && (
                    <div><Label>Squad / team</Label>
                      <Input data-testid="ts-squad" value={f.squad_name} onChange={(e) => setF({ ...f, squad_name: e.target.value })} placeholder="e.g. Driftmoor U19" className={`mt-1.5 ${TOUCH}`} /></div>
                  )}
                  <div><Label>Location</Label>
                    <GroundPicker
                      inputTestId="ts-location"
                      testIdPrefix="ts-location"
                      value={f.location}
                      groundId={f.ground_id}
                      onChange={(name, groundId) => {
                        setF((prev) => ({
                          ...prev,
                          location: name,
                          ground_id: groundId,
                          // #166/#423. THE PAIR RULE, EXPRESSED IN THE UI.
                          // A net belongs to one venue; move the venue and the
                          // net you picked at the old one is not there. The
                          // API refuses that pair (`facility_not_found`), but
                          // the user should never reach the refusal — the
                          // surface control below simply re-populates from the
                          // new ground with nothing selected.
                          facility_id: facilityAfterGroundChange(prev.ground_id, groundId, prev.facility_id),
                        }));
                        setFieldErrors((err) => ({ ...err, location: undefined }));
                        // #581 — the surface cleared with the venue; its rating goes too.
                        setRatingDraft(draftFromOwnRating(null));
                      }}
                      placeholder="e.g. Corvale nets"
                      disabled={locked}
                      markFieldErrors={newLook /* #1307 */}
                      // #1405 — the new look resolves on blur or a pick, not per keystroke,
                      // so a typed space after a ground's whole name is not trimmed away.
                      // The old look keeps resolving as you type (D217).
                      autoResolveWhileTyping={!newLook}
                      className={`mt-1.5 ${TOUCH} ${oldRed("location", "border-red-500 focus-visible:ring-red-500")}`}
                      // #1305 (D303, D312): the new look marks the field and keeps
                      // the list shut when Save's focus move lands here.
                      {...(newLook
                        ? {
                            ariaInvalid: fieldErrors.location ? "true" : undefined,
                            ariaDescribedBy: fieldErrors.location ? "ts-location-error" : undefined,
                            inputStyle: fieldErrors.location ? INVALID_STYLE : undefined,
                            openOnProgrammaticFocus: false,
                          }
                        : {})}
                    />
                    {fieldError("location", "ts-location-error", "text-xs text-red-600 mt-1")}</div>
                  {/* FAC-06's second clause. Renders NOTHING until a ground is
                      picked, and nothing at all for a ground with no
                      registered surfaces — which is the common case and must
                      look exactly as it did before this slice. */}
                  <FacilityPicker
                    groundId={f.ground_id}
                    value={f.facility_id}
                    storedLabel={editing ? facilityOptionLabel({ name: editing.facility_name, surface: editing.facility_surface }) : null}
                    onChange={(facilityId) => {
                      setF((prev) => ({ ...prev, facility_id: facilityId }));
                      // #581 — a new surface is a new thing to rate.
                      setRatingDraft(draftFromOwnRating(null));
                    }}
                    disabled={locked}
                  />
                  {/* #581 — rate the picked net/field without leaving the
                      form. Only for a player logging their OWN session: a
                      coach scheduling for someone else is not the person who
                      played, and the played-here gate would 403 their PUT.
                      Renders only when a specific surface is selected. Saved
                      with the session (POST session → PUT rating). */}
                  {!isCoach && (
                    <InlineFacilityRating
                      facility={selectedFacility}
                      draft={ratingDraft}
                      setDraft={setRatingDraft}
                      savedWith="session"
                      disabled={locked}
                      testId="ts-facility-rating"
                      tone={newLook ? "saved" : undefined}
                    />
                  )}
                  <div><Label>Duration (minutes)</Label>
                    <Input data-testid="ts-duration" type="number" inputMode="numeric" min={0} value={f.duration_minutes} disabled={locked} onChange={(e) => setF({ ...f, duration_minutes: sanitizeDuration(e.target.value) })} className={`mt-1.5 ${TOUCH}`} /></div>
                  <div><Label>Training category{required.fields.includes("category") && " *"}</Label>
                    <Select value={f.category} onValueChange={(v) => { setF({ ...f, category: v }); setFieldErrors((err) => ({ ...err, category: undefined })); }}>
                      <SelectTrigger data-testid="ts-category" className={`mt-1.5 ${TOUCH} ${oldRed("category", "border-red-500 ring-1 ring-red-500")}`} {...invalidProps("category", "ts-category-error")}><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                    {fieldError("category", "ts-category-error", "text-xs text-red-600 mt-1")}
                  </div>
                  <div className="md:col-span-2"><Label>Skill / topic {isCoach ? "to focus on" : "worked on"}</Label>
                    <Input data-testid="ts-skill" value={f.skill_topic} onChange={(e) => setF({ ...f, skill_topic: e.target.value })} placeholder="e.g. Playing spin off the back foot" className={`mt-1.5 ${TOUCH}`} /></div>
                </div>

                {/* Reflections — player-only (issue #75: a coach may schedule
                    a session but never records how it went; the API rejects
                    these fields from a coach regardless of this UI), and
                    only once the session has happened (#83: hidden, not
                    optional — the API writes them null on a planned create
                    whatever a client sends). */}
                {showReflection && (
                  <>
                    <div className="space-y-3">
                      <div><Label>Lesson learned</Label><Textarea data-testid="ts-lesson" rows={2} value={f.lesson_learned} onChange={(e) => setF({ ...f, lesson_learned: e.target.value })} className="mt-1.5" /></div>
                      <div><Label>What worked well</Label><Textarea data-testid="ts-worked" rows={2} value={f.what_worked_well} onChange={(e) => setF({ ...f, what_worked_well: e.target.value })} className="mt-1.5" /></div>
                      <div><Label>Areas for improvement</Label><Textarea data-testid="ts-improve" rows={2} value={f.areas_for_improvement} onChange={(e) => setF({ ...f, areas_for_improvement: e.target.value })} className="mt-1.5" /></div>
                      <div><Label>Next focus area</Label><Textarea data-testid="ts-focus" rows={2} value={f.next_focus_area} onChange={(e) => setF({ ...f, next_focus_area: e.target.value })} className="mt-1.5" /></div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-3">
                      <RatingBlock label="Confidence" value={f.confidence_rating} onChange={(v) => setF({ ...f, confidence_rating: v })} color={GOLD} icon={Flame} tid="ts-confidence" />
                      <RatingBlock label="Focus" value={f.focus_rating} onChange={(v) => setF({ ...f, focus_rating: v })} color={NAVY} icon={Zap} tid="ts-focus-rating" />
                      <RatingBlock label="Motivation" value={f.motivation_rating} onChange={(v) => setF({ ...f, motivation_rating: v })} color={GREEN} icon={Smile} tid="ts-motivation" />
                    </div>

                    <div><Label>Mindset / mental-skills reflection</Label><Textarea data-testid="ts-mindset" rows={2} value={f.mindset_reflection} onChange={(e) => setF({ ...f, mindset_reflection: e.target.value })} className="mt-1.5" /></div>
                    <div><Label>Overall session reflection</Label><Textarea data-testid="ts-overall" rows={3} value={f.overall_reflection} onChange={(e) => setF({ ...f, overall_reflection: e.target.value })} className="mt-1.5" /></div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label>Action items</Label>
                        <Button size="sm" variant="ghost" className="h-11 sm:h-8" onClick={addActionItem} data-testid="ts-add-action"><Plus size={12} className="mr-1" /> Add</Button>
                      </div>
                      <div className="space-y-2">
                        {f.action_items.map((a, i) => (
                          <div key={i} className="flex gap-2">
                            <Input data-testid={`ts-action-${i}`} value={a} onChange={(e) => setActionItem(i, e.target.value)} placeholder={`Action item ${i + 1}`} className={`flex-1 ${TOUCH}`} />
                            <Button size="icon" variant="ghost" className={`shrink-0 ${TOUCH_ICON}`} onClick={() => removeActionItem(i)}><X size={14} /></Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {coachBoard && saveError && <CoachSaveFailed />}
                <Button onClick={save} disabled={busy || !canSubmitRequired(required.status) || (isCoach && !effectivePlayerId)} className={`w-full ${TOUCH}`} style={{ background: NAVY }} data-testid="ts-save">
                  {busy
                    ? <><Loader2 size={16} className="mr-2 animate-spin" /> Saving…</>
                    : saveLabel({ completing, editingId, isCoach, planningAhead })}
                </Button>
                {/* #1281 (D276): the board's card has no bin, so on the new look
                    delete lives here, when editing, for a player. Same
                    `remove` and the same confirm. */}
                {boardCards && editingId && editing && (
                  <Button
                    type="button" variant="outline" className={`w-full ${TOUCH}`} data-testid="ts-dialog-delete"
                    onClick={async () => { if (await remove(editing)) closeDialog(false); }}
                  >
                    Delete session
                  </Button>
                )}
              </div>
            </DialogContent>
          </Dialog>
  );

  // The session list: the empty state, or the cards.
  const list = sessionsList({
    boardCards, coachBoard, isCoach, loadState, loadedFor, rosterState, roster, items, filter, contextFilter,
    coachPlayerId, load, retryCoach, openEdit, remove,
  });

  // Issue #1128 (TrainingHome board, D243; D219): the two-section look. The one
  // gold Capture session leads, the list follows, and the two filters wait
  // behind More as the board's labelled pickers (Show, Category). The shipped
  // layout below is for the old look and is untouched (D217).
  if (newLook) {
    return (
      <SectionsLayout
        isCoach={isCoach}
        rosterState={rosterState}
        scheduledLine={coachBoard ? scheduledLine : null}
        showSchedule={!isCoach || roster.length > 0}
        dialog={dialog}
        list={list}
        roster={roster}
        coachPlayerId={coachPlayerId}
        setCoachPlayerId={setCoachPlayerId}
        context={{ value: contextFilter, onChange: setContextFilter }}
        category={{ value: filter, onChange: setFilter }}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="training-sessions">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Sparkles size={14} color={GOLD} />
          <span className="text-sm text-slate-500">
            {isCoach
              ? "Schedule a session for a linked player — they add the reflection once it's done."
              : "Capture lessons after every training session."}
          </span>
        </div>
        {/* Issue #123/#126: this row doesn't wrap and both Selects are a bare
            `w-48` (192px) — fine on desktop, but a coach's variant (two
            Selects + the trigger button) sums to ~588px against ~342px of
            usable width at 390px. Mobile-first: full width + TOUCH height on
            phone, the original 192px/h-10 desktop density back at `sm:`. */}
        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          {isCoach && roster.length > 0 && (
            <Select value={coachPlayerId} onValueChange={setCoachPlayerId}>
              <SelectTrigger className={`w-full sm:w-48 ${TOUCH}`} data-testid="ts-coach-player"><SelectValue placeholder="Select player" /></SelectTrigger>
              <SelectContent>
                {roster.map((p) => <SelectItem key={p.player_id} value={p.player_id}>{p.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {/* Issue #152: two filters where there was one, and they narrow
              independently — "my 1:1 coaching, batting" has to be askable.
              Both keep the same `w-full sm:w-48` mobile-first sizing #123/#126
              established, so at 390px they stack full-width instead of
              summing past the ~342px of usable width. */}
          <Select value={contextFilter} onValueChange={setContextFilter}>
            <SelectTrigger className={`w-full sm:w-48 ${TOUCH}`} data-testid="ts-context-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_CONTEXTS}>All training</SelectItem>
              {SESSION_CONTEXTS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className={`w-full sm:w-48 ${TOUCH}`} data-testid="ts-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          {dialog}
        </div>
      </div>

      {/* List */}
      {list}
    </div>
  );
}

// The dialog's heading and the Save button's idle label (#1546: out of the component to lower its branch count).
function dialogTitle({ completing, editingId, isCoach, planningAhead }) {
  if (completing) return "How did it go?";
  if (editingId) return "Update training session";
  if (isCoach) return "Schedule a session";
  return planningAhead ? "Plan a training session" : "Training session capture";
}
function saveLabel({ completing, editingId, isCoach, planningAhead }) {
  if (completing) return "Save reflection";
  if (editingId) return "Update session";
  if (isCoach) return "Schedule session";
  return planningAhead ? "Plan session" : "Save session";
}

/**
 * The session list for whichever layout is showing: the coach's board, the player's board, or the shipped
 * empty state and cards. Pulled out of `TrainingSessions` (#1546) so the component's branch count falls; it reads
 * the same values the component held, and returns an element, so the output is unchanged.
 */
function sessionsList({
  boardCards, coachBoard, isCoach, loadState, loadedFor, rosterState, roster, items, filter, contextFilter,
  coachPlayerId, load, retryCoach, openEdit, remove,
}) {
  const boardList = boardCards ? (
    loadState === "loading" ? <BoardLoading />
      : loadState === "failed" ? <BoardLoadFailed onRetry={load} />
      : items.length === 0 ? (filter === "all" && contextFilter === ALL_CONTEXTS ? <BoardEmpty /> : <BoardFilteredEmpty />)
      : (
        <div data-testid="ts-list" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {items.map((s) => (
            <BoardSessionCard key={s.session_id} s={s} onOpen={() => openEdit(s)} onAddReflection={() => openEdit(s)} />
          ))}
        </div>
      )
  ) : null;
  const coachName = roster.find((p) => p.player_id === coachPlayerId)?.full_name || "";
  const coachList = coachBoard ? (
    <CoachSessionsList
      rosterState={rosterState} loadState={loadState} loadedFor={loadedFor} roster={roster} items={items}
      playerId={coachPlayerId} playerName={coachName} filtered={filter !== "all" || contextFilter !== ALL_CONTEXTS}
      onRetry={retryCoach}
    />
  ) : null;
  const list = coachList || boardList || (
      items.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3" style={{ background: `${GOLD}20` }}>
            <Target size={22} color={GOLD} />
          </div>
          <p className="text-slate-500">
            {isCoach
              ? (roster.length === 0
                ? "You have no linked players yet."
                : "No sessions scheduled yet. Click \"Schedule a session\" to plan the first one.")
              : "No training sessions captured yet. Click \"Capture session\" to log your first one."}
          </p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4" data-testid="ts-list">
          {items.map((s) => (
            <SessionCard
              key={s.session_id}
              s={s}
              // Edit/delete stay player-only server-side (issue #75 only opens
              // CREATE to a coach) — hide the actions a coach's click would
              // just get a 403 on.
              canEdit={!isCoach}
              onEdit={() => openEdit(s)}
              onDelete={() => remove(s)}
            />
          ))}
        </div>
      )
  );
  return list;
}

/**
 * One of the board's two pickers: a small caps label over the chosen value in a
 * white box 46px tall (TrainingHome.dc.html, "More, open"). A Radix Select, so
 * the keyboard and the menu are the shipped ones; only the trigger is drawn.
 */
function SectionsLayout({ isCoach, rosterState, scheduledLine, showSchedule, dialog, list, roster, coachPlayerId, setCoachPlayerId, context, category }) {
  const coachPicker = isCoach && roster.length > 0;
  return (
    <div className="space-y-6" data-testid="training-sessions">
      {/* #1159: a coach reads one player at a time, so the Player picker leads
          the face (it was behind More), above the one gold button. With no
          players the button is not drawn at all. */}
      {coachPicker && (
        <div className="flex">
          <LabelledPicker label="Player" value={coachPlayerId} onValueChange={setCoachPlayerId} testId="ts-coach-player" placeholder="Select player">
            {roster.map((p) => <SelectItem key={p.player_id} value={p.player_id}>{p.full_name}</SelectItem>)}
          </LabelledPicker>
        </div>
      )}
      {showSchedule && dialog}
      {scheduledLine && <CoachScheduledLine>{scheduledLine}</CoachScheduledLine>}
      {list}
      {/* A coach with no linked players has nothing to filter: the board shows the card alone (#1159). */}
      {!(isCoach && rosterState === "ready" && roster.length === 0) && (
      <MoreSection screen="learn" data-testid="learn-more">
        <div className="flex flex-col gap-3">
          <div className="flex gap-2">
            <LabelledPicker label="Show" value={context.value} onValueChange={context.onChange} testId="ts-context-filter">
              <SelectItem value={ALL_CONTEXTS}>All training</SelectItem>
              {SESSION_CONTEXTS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </LabelledPicker>
            <LabelledPicker label="Category" value={category.value} onValueChange={category.onChange} testId="ts-filter">
              <SelectItem value="all">All</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </LabelledPicker>
          </div>
        </div>
      </MoreSection>
      )}
    </div>
  );
}

function LabelledPicker({ label, value, onValueChange, testId, placeholder, children }) {
  const uid = useId();
  const labelId = `${uid}-label`;
  const valueId = `${uid}-value`;
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        className="h-auto flex-1 min-w-0 min-h-[46px] py-0 px-[10px] gap-[6px] bg-white"
        style={{ borderColor: "var(--nw-line)", borderRadius: "var(--nw-radius-control)" }}
        data-testid={testId}
        aria-labelledby={`${labelId} ${valueId}`}
      >
        {/* A div, not a span: ui/select.jsx's `[&>span]:line-clamp-1` clamps a
            direct child span to one line and set the label inline with the value. */}
        <div className="flex flex-col min-w-0 text-left">
          <span className="nw-section" id={labelId} style={{ fontSize: 10, letterSpacing: "0.12em", display: "block" }}>{label}</span>
          <span className="nw-body-strong truncate" id={valueId} style={{ fontSize: 14, display: "block" }}><SelectValue placeholder={placeholder} /></span>
        </div>
      </SelectTrigger>
      <SelectContent>{children}</SelectContent>
    </Select>
  );
}

function RatingBlock({ label, value, onChange, color, icon: Icon, tid }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={14} color={color} />
        <Label className="text-xs">{label}</Label>
        <span className="ml-auto font-display font-black text-lg" style={{ color }}>{value}</span>
        <span className="text-xs text-slate-400">/10</span>
      </div>
      <input type="range" min={1} max={10} value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={tid}
        className="w-full accent-current" style={{ color }} />
    </div>
  );
}

const CONTEXT_COLORS = { coaching: PURPLE, team: BLUE };

function SessionCard({ s, canEdit = true, onEdit, onDelete }) {
  const catColor = CATEGORY_COLORS[s.category] || NAVY;
  // Issue #152: `casual` gets NO badge. Every session recorded before this
  // ticket is casual by definition, and a badge on all of them would be this
  // feature changing the look of history it did not change the meaning of —
  // the AC is that existing rows render unchanged. A badge here says "this
  // one is not the ordinary case", which is the only thing worth the pixels.
  const contextColor = CONTEXT_COLORS[s.session_context];
  // Issue #83: `completed` gets no badge, for the reason `casual` gets none
  // — every session recorded before this ticket is completed by definition,
  // and badging all of them would restyle history whose meaning did not
  // change. A planned session is badged, and one whose date has passed is
  // badged differently, because that one is asking for something.
  const status = statusBadge(s);
  const overdue = needsReflection(s);
  const avgRating = ((s.confidence_rating + s.focus_rating + s.motivation_rating) / 3).toFixed(1);
  // A coach-scheduled session (issue #75) has every reflection field null/
  // empty until the player fills it in — the numeric ratings still carry
  // their DB defaults either way, so gate the ratings/reflection blocks on
  // actual free-text content rather than showing a misleading "5/10" for a
  // session nobody has reflected on yet.
  const hasReflection = Boolean(
    s.lesson_learned || s.what_worked_well || s.areas_for_improvement || s.next_focus_area ||
    s.mindset_reflection || s.overall_reflection || (s.action_items && s.action_items.length > 0),
  );
  return (
    // `min-w-0` — issue #251, and the concrete instance is #350. This card
    // is a child of the `ts-list` grid, and a grid item defaults to
    // `min-width: auto`: it refuses to shrink below its own min-content, so
    // the widest thing inside it sets the track and the PAGE scrolls
    // sideways. The widest thing here is not prose (prose wraps, so its
    // min-content is one word) — it is the `truncate` on the skill-topic
    // badge and on the title below, because `truncate` sets
    // `white-space: nowrap`, which makes their min-content the WHOLE string.
    // `max-w-full` on that badge cannot save it: a percentage max-width
    // resolves against a parent that has itself already grown. With
    // `min-w-0` the track shrinks to the viewport first, and only then do
    // the truncations resolve against a real width — which is what they
    // were written to do.
    <Card
      className={`min-w-0 hover:shadow-md transition-all ${overdue ? "border-2" : "border-slate-200"}`}
      style={overdue ? { borderColor: `${GOLD}66` } : undefined}
      data-testid={`ts-card-${s.session_id}`}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 mb-1">
              <Badge style={{ background: `${catColor}15`, color: catColor }}>{s.category}</Badge>
              {contextColor && (
                <Badge style={{ background: `${contextColor}15`, color: contextColor }} data-testid={`ts-context-badge-${s.session_id}`}>
                  {contextLabel(s.session_context)}
                </Badge>
              )}
              {s.skill_topic && <Badge variant="outline" className="max-w-full truncate">{s.skill_topic}</Badge>}
              {status && (
                <Badge
                  style={{ background: `${status.color}18`, color: status.color }}
                  data-testid={`ts-status-${status.testid}-${s.session_id}`}
                >
                  {status.label}
                </Badge>
              )}
              {/* Pre-#83 badge, now narrowed: a COMPLETED session with no
                  reflection text is still worth flagging (that is a
                  coach-scheduled session from #75 that the player logged
                  through), but on a planned row it would say the same thing
                  as the status badge directly above it, twice. */}
              {!hasReflection && !status && (
                <Badge variant="outline" className="text-[10px]" style={{ color: "#64748B" }}>Awaiting reflection</Badge>
              )}
            </div>
            <h3 className="font-display font-bold text-lg text-slate-900 truncate">{s.session_title}</h3>
            <div className="flex flex-wrap gap-3 text-xs text-slate-500 mt-1">
              {/* #463. The date, plus the time when one was set. One span,
                  not two: "Thursday, 4pm" is a single fact, and a second
                  chip would sit next to the DURATION's clock icon and read
                  as a competing one. Sessions with no time render exactly
                  as they did before this ticket. */}
              <span className="flex items-center gap-1" data-testid={`ts-when-${s.session_id}`}>
                <Calendar size={11} /> {dateTimeLabel(s.training_date, s.training_time)}
              </span>
              {s.duration_minutes > 0 && <span className="flex items-center gap-1"><Clock size={11} /> {s.duration_minutes} min</span>}
              {s.coach_name && <span className="flex items-center gap-1"><User size={11} /> {s.coach_name}</span>}
              {s.squad_name && <span className="flex items-center gap-1"><Users size={11} /> {s.squad_name}</span>}
              {s.location && <span className="flex items-center gap-1"><MapPin size={11} /> {s.location}</span>}
              {/* FAC-06 (#166): WHICH SURFACE, beside where. Both roles see
                  this card — a coach reading their own scheduled session and
                  the player who is going to turn up to it — so one line here
                  answers "which net" for both. Absent on every session that
                  records no surface, which is most of them: a venue with no
                  registered nets renders exactly as it did before. The name
                  is `facility_name`, derived server-side on the read path,
                  never a UUID and never a second fetch. */}
              {s.facility_name && (
                <span className="flex items-center gap-1" data-testid={`ts-surface-${s.session_id}`}>
                  <Grid2x2 size={11} /> {facilityOptionLabel({ name: s.facility_name, surface: s.facility_surface })}
                </span>
              )}
            </div>
          </div>
          {canEdit && (
            <div className="flex items-center gap-1">
              <Button size="icon" variant="ghost" onClick={onEdit} title={overdue ? "Add your reflection" : "Edit session"} data-testid={`ts-edit-${s.session_id}`}><Pencil size={14} /></Button>
              <Button size="icon" variant="ghost" onClick={onDelete} title="Delete session" data-testid={`ts-del-${s.session_id}`}><Trash2 size={14} /></Button>
            </div>
          )}
        </div>

        {hasReflection && (
          <>
            {/* Ratings row */}
            <div className="grid grid-cols-4 gap-2 mt-4 text-center">
              <Rating icon={Flame} label="Conf" value={s.confidence_rating} color={GOLD} />
              <Rating icon={Zap} label="Focus" value={s.focus_rating} color={NAVY} />
              <Rating icon={Smile} label="Motiv" value={s.motivation_rating} color={GREEN} />
              <Rating icon={Sparkles} label="Avg" value={avgRating} color={PURPLE} />
            </div>

            {/* Sections */}
            <div className="mt-4 pt-4 border-t border-slate-100 space-y-3 text-sm">
              {s.lesson_learned && <Row label="Lesson learned" v={s.lesson_learned} />}
              {s.what_worked_well && <Row label="What worked well" v={s.what_worked_well} color={GREEN} />}
              {s.areas_for_improvement && <Row label="Areas for improvement" v={s.areas_for_improvement} color="#EF4444" />}
              {s.next_focus_area && <Row label="Next focus" v={s.next_focus_area} color={GOLD} />}
              {s.mindset_reflection && <Row label="Mindset" v={s.mindset_reflection} icon={Brain} color={PURPLE} />}
              {s.overall_reflection && <Row label="Overall reflection" v={s.overall_reflection} />}
            </div>

            {s.action_items?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Action items</div>
                <ul className="space-y-1.5">
                  {s.action_items.map((a, i) => (
                    <li key={i} className="text-sm text-slate-700 flex gap-2">
                      <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: GOLD }} />{a}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Row({ label, v, color, icon: Icon }) {
  return (
    <div>
      <div className="flex items-center gap-1.5">
        {Icon && <Icon size={11} color={color || "#64748B"} />}
        <div className="text-[10px] uppercase tracking-widest" style={{ color: color || "#64748B" }}>{label}</div>
      </div>
      <div className="text-sm text-slate-800 mt-0.5">{v}</div>
    </div>
  );
}

function Rating({ icon: Icon, label, value, color }) {
  return (
    <div className="p-2 rounded-lg" style={{ background: `${color}10` }}>
      <div className="flex items-center justify-center gap-1">
        <Icon size={11} color={color} />
        <span className="text-[10px] uppercase text-slate-500">{label}</span>
      </div>
      <div className="font-display font-black text-lg" style={{ color }}>{value}</div>
    </div>
  );
}
